import jsep from 'jsep';

/**
 * Safe arithmetic formula parsing.
 *
 * jsep produces a small AST which is then structurally validated against a
 * whitelist — there is no `eval`, no `Function`, and no property access on
 * anything other than caller-constructed context objects. Anything outside
 * the whitelist fails at parse time so bad input is rejected when it is
 * authored, not when it is evaluated.
 */

// One-time configuration of the shared jsep singleton. This library is the
// sole owner of jsep in the monorepo — consumers must import formula parsing
// from here rather than configuring jsep themselves, otherwise they would
// silently inherit (or fight over) this grammar.
// Bitwise/shift operators are not part of the formula language.
['|', '^', '&', '<<', '>>', '>>>'].forEach((op) => jsep.removeBinaryOp(op));
jsep.removeUnaryOp('~');
// Exponentiation, right-associative (same precedence JS gives `**`)
jsep.addBinaryOp('**', 11, true);

export const ALLOWED_BINARY_OPERATORS = new Set([
  '+',
  '-',
  '*',
  '/',
  '%',
  '**',
  '<',
  '<=',
  '>',
  '>=',
  '==',
  '!=',
  '===',
  '!==',
  '&&',
  '||',
]);

export const ALLOWED_UNARY_OPERATORS = new Set(['-', '+', '!']);

const DEFAULT_MAX_DEPTH = 50;

export class FormulaParseError extends Error {
  constructor(
    message: string,
    public readonly expression: string,
  ) {
    super(message);
    this.name = 'FormulaParseError';
  }
}

export interface ParseFormulaOptions {
  /**
   * When provided, a call to any function outside this set is a parse error.
   * Catches typos at authoring time instead of evaluation time.
   */
  allowedFunctions?: ReadonlySet<string>;
  /**
   * Function names whose calls must have exactly one string-literal argument
   * so the argument is statically resolvable (e.g. field-path lookups).
   */
  staticStringArgFunctions?: ReadonlySet<string>;
  /**
   * Extra validation applied to each static string argument, e.g. verifying
   * that it parses as a field path. Throw to reject the formula.
   */
  validateStaticStringArg?: (functionName: string, argValue: string) => void;
  /**
   * Maximum AST nesting depth. Guards against stack exhaustion from
   * pathologically nested authored input.
   */
  maxDepth?: number;
}

/**
 * Depth-first walk over every node of a jsep AST
 */
export const walkFormulaAst = (
  node: jsep.Expression,
  visitor: (node: jsep.Expression, depth: number) => void,
  depth = 0,
): void => {
  visitor(node, depth);
  const childDepth = depth + 1;
  switch (node.type) {
    case 'BinaryExpression': {
      const binary = node as jsep.BinaryExpression;
      walkFormulaAst(binary.left, visitor, childDepth);
      walkFormulaAst(binary.right, visitor, childDepth);
      break;
    }
    case 'UnaryExpression': {
      walkFormulaAst(
        (node as jsep.UnaryExpression).argument,
        visitor,
        childDepth,
      );
      break;
    }
    case 'ConditionalExpression': {
      const conditional = node as jsep.ConditionalExpression;
      walkFormulaAst(conditional.test, visitor, childDepth);
      walkFormulaAst(conditional.consequent, visitor, childDepth);
      walkFormulaAst(conditional.alternate, visitor, childDepth);
      break;
    }
    case 'CallExpression': {
      const call = node as jsep.CallExpression;
      walkFormulaAst(call.callee, visitor, childDepth);
      call.arguments.forEach((arg) => walkFormulaAst(arg, visitor, childDepth));
      break;
    }
    case 'MemberExpression': {
      const member = node as jsep.MemberExpression;
      walkFormulaAst(member.object, visitor, childDepth);
      // property is an Identifier for non-computed access; do not walk it as
      // an expression — it is a name, not a value reference
      break;
    }
    default:
      break;
  }
};

const validateNode = (
  node: jsep.Expression,
  source: string,
  options: ParseFormulaOptions,
): void => {
  switch (node.type) {
    case 'Literal': {
      const { value } = node as jsep.Literal;
      const valueType = typeof value;
      if (
        valueType !== 'number' &&
        valueType !== 'string' &&
        valueType !== 'boolean'
      ) {
        throw new FormulaParseError(
          `Unsupported literal "${String(value)}" — only numbers, strings, and booleans are allowed`,
          source,
        );
      }
      return;
    }
    case 'Identifier':
      return;
    case 'BinaryExpression': {
      const { operator } = node as jsep.BinaryExpression;
      if (!ALLOWED_BINARY_OPERATORS.has(operator)) {
        throw new FormulaParseError(
          `Operator "${operator}" is not supported`,
          source,
        );
      }
      return;
    }
    case 'UnaryExpression': {
      const { operator } = node as jsep.UnaryExpression;
      if (!ALLOWED_UNARY_OPERATORS.has(operator)) {
        throw new FormulaParseError(
          `Operator "${operator}" is not supported`,
          source,
        );
      }
      return;
    }
    case 'ConditionalExpression':
      return;
    case 'CallExpression': {
      const call = node as jsep.CallExpression;
      if (call.callee.type !== 'Identifier') {
        throw new FormulaParseError(
          'Only direct function calls are supported (e.g. max(a, b))',
          source,
        );
      }
      const calleeName = (call.callee as jsep.Identifier).name;
      if (
        options.allowedFunctions &&
        !options.allowedFunctions.has(calleeName)
      ) {
        throw new FormulaParseError(`Unknown function "${calleeName}"`, source);
      }
      if (options.staticStringArgFunctions?.has(calleeName)) {
        const [firstArg] = call.arguments;
        if (
          call.arguments.length !== 1 ||
          firstArg?.type !== 'Literal' ||
          typeof (firstArg as jsep.Literal).value !== 'string'
        ) {
          throw new FormulaParseError(
            `${calleeName}() requires exactly one string literal argument, e.g. ${calleeName}('quote.amount')`,
            source,
          );
        }
        if (options.validateStaticStringArg) {
          const argValue = (firstArg as jsep.Literal).value as string;
          try {
            options.validateStaticStringArg(calleeName, argValue);
          } catch (ex) {
            throw new FormulaParseError(
              `Invalid argument for ${calleeName}("${argValue}"): ${ex instanceof Error ? ex.message : String(ex)}`,
              source,
            );
          }
        }
      }
      return;
    }
    case 'MemberExpression': {
      const member = node as jsep.MemberExpression;
      if (member.computed) {
        throw new FormulaParseError(
          'Computed member access (obj[expr]) is not supported',
          source,
        );
      }
      if (member.property.type !== 'Identifier') {
        throw new FormulaParseError(
          'Member access must use a plain property name',
          source,
        );
      }
      return;
    }
    default:
      throw new FormulaParseError(`Unsupported syntax: ${node.type}`, source);
  }
};

/**
 * Parse a formula and structurally validate it against the whitelist.
 * Throws {@link FormulaParseError} for anything outside the language.
 */
export const parseFormula = (
  source: string,
  options: ParseFormulaOptions = {},
): jsep.Expression => {
  let ast: jsep.Expression;
  try {
    ast = jsep(source);
  } catch (ex) {
    throw new FormulaParseError(
      ex instanceof Error ? ex.message : 'Failed to parse formula',
      source,
    );
  }
  const maxDepth = options.maxDepth ?? DEFAULT_MAX_DEPTH;
  walkFormulaAst(ast, (node, depth) => {
    if (depth > maxDepth) {
      throw new FormulaParseError(
        `Formula is nested more than ${maxDepth} levels deep`,
        source,
      );
    }
    validateNode(node, source, options);
  });
  return ast;
};
